import { useAuth } from '@/app/context';
import type { GetIssuedPremiumsParams, IssuedPremium } from '@/services';
import { useDataTablePageSync } from '@/shared/hooks';
import { formatCurrency } from '@/shared/utils/currencyFormatter';
import { formatDateToBrazil } from '@/shared/utils/format-date.util';
import type { UserType } from '@/shared/utils/get-group';
import { f, useFilters } from '@/shared/utils';
import { SearchFilterField } from '@/shared/ui';
import {
  Button,
  DataTable,
  DataTableColumn,
  Pagination,
  Tag,
} from '@noorden-insurtech/alba-design-system';
import { Calendar, Download, Funnel, FunnelX } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  CardRow,
  ReportCard,
  ReportCardBody,
  ReportCardHeader,
  ReportCardSkeleton,
} from '../shared/report-mobile-card.component';
import {
  useAdvisoryOptions,
  useBrokerOptions,
  useReportsFilterOptions,
} from '../../hooks/use-reports-filter-options.hook';
import { useIssuedPremiums } from '../../hooks/use-issued-premiums.hook';
import { useIssuedPremiumsSummary } from '../../hooks/use-issued-premiums-summary.hook';
import { useIssuedPremiumsExport } from '../../hooks/use-issued-premiums-export.hook';
import { PremiosEmitidosFilters } from './premios-emitidos-filters.component';
import { SummaryCards } from './summary-cards.component';

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50];

/** Colunas ordenáveis: id da coluna → chave de ordenação do backend. */
const SORT_FIELD_BY_COLUMN: Record<string, string> = {
  advisory: 'assessoria',
  broker: 'corretor',
  product: 'produto',
  client: 'cliente',
  policy: 'apolice',
  issue: 'emissao',
  status: 'status',
};
const COLUMN_BY_SORT_FIELD: Record<string, string> = Object.fromEntries(
  Object.entries(SORT_FIELD_BY_COLUMN).map(([col, field]) => [field, col])
);

function deriveUserType(groups: string[] | null): UserType | null {
  const list = groups ?? [];
  if (list.some(g => g.startsWith('insurer.'))) return 'insurer';
  if (list.some(g => g.startsWith('advisory.'))) return 'advisory';
  if (list.some(g => g.startsWith('broker.'))) return 'broker';
  return null;
}

function addMonths(iso: string, months: number): string {
  const [year, month, day] = iso.split('-').map(Number);
  const base = new Date(Date.UTC(year, month - 1 + months, 1));
  const lastDay = new Date(
    Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + 1, 0)
  ).getUTCDate();
  const clampedDay = Math.min(day, lastDay);
  return new Date(
    Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), clampedDay)
  )
    .toISOString()
    .slice(0, 10);
}

function validatePeriod(from: string, to: string): string | undefined {
  if (!from || !to) return undefined;
  if (to < from)
    return 'A data final do período deve ser maior ou igual à inicial.';
  if (to > addMonths(from, 6)) return 'Selecione um período de até 6 meses';
  return undefined;
}

const money = (value: number | null) => formatCurrency(Number(value ?? 0));

export function PremiosEmitidosTab() {
  const { groups } = useAuth();
  const userType = deriveUserType(groups);

  const [defaultPeriod] = useState(() => {
    const to = new Date();
    const from = new Date();
    from.setMonth(from.getMonth() - 1);
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    return { from: iso(from), to: iso(to) };
  });

  const { filters, setFilters, registerFilter } = useFilters({
    page: f.integer().withDefault(1),
    pageSize: f.integer().withDefault(10),
    sortField: f.string().withDefault(''),
    sortType: f.string().withDefault('asc'),
    search: f.string().withDefault('').withDebounce(500),
    issueDateFrom: f.string().withDefault(defaultPeriod.from),
    issueDateTo: f.string().withDefault(defaultPeriod.to),
    advisoryPartyCode: f.string().withDefault(''),
    brokerPartyCode: f.string().withDefault(''),
    productCode: f.string().withDefault(''),
    branchCode: f.string().withDefault(''),
    statusCode: f.string().withDefault(''),
  });

  const [showFilters, setShowFilters] = useState(false);

  // "Limpar Seleção" só aparece quando algum filtro do painel diverge do
  // padrão (a busca livre fica fora do painel e não conta).
  const hasActiveFilter =
    Boolean(
      filters.advisoryPartyCode ||
      filters.brokerPartyCode ||
      filters.productCode ||
      filters.branchCode ||
      filters.statusCode
    ) ||
    filters.issueDateFrom !== defaultPeriod.from ||
    filters.issueDateTo !== defaultPeriod.to;

  const periodError = validatePeriod(
    filters.issueDateFrom,
    filters.issueDateTo
  );
  const periodReady = Boolean(
    filters.issueDateFrom && filters.issueDateTo && !periodError
  );
  const dataTableRef = useDataTablePageSync<IssuedPremium>(filters.page);

  // Filtros que afetam o conjunto (grid e cards). branchCode/statusCode são
  // números no backend; no estado da tela ficam como string (valor do combobox).
  const activeFilters = {
    issueDateFrom: filters.issueDateFrom,
    issueDateTo: filters.issueDateTo,
    advisoryPartyCode: filters.advisoryPartyCode || undefined,
    brokerPartyCode: filters.brokerPartyCode || undefined,
    productCode: filters.productCode || undefined,
    branchCode: filters.branchCode ? Number(filters.branchCode) : undefined,
    statusCode: filters.statusCode ? Number(filters.statusCode) : undefined,
    search: filters.search || undefined,
  };

  const queryParams: GetIssuedPremiumsParams = {
    ...activeFilters,
    page: filters.page,
    pageSize: filters.pageSize,
    sortField: filters.sortField || undefined,
    sortType: filters.sortType === 'desc' ? 'desc' : 'asc',
  };

  const {
    data: results,
    isLoading,
    error,
  } = useIssuedPremiums(periodReady ? queryParams : {});

  // Cards: dependem SÓ dos filtros — paginar/ordenar não refaz a agregação.
  const { data: summary, isLoading: summaryLoading } = useIssuedPremiumsSummary(
    periodReady ? activeFilters : {}
  );

  const { data: advisoryOptions = [] } = useAdvisoryOptions(
    userType === 'insurer'
  );
  const { data: brokerOptions = [] } = useBrokerOptions(
    userType === 'insurer' || userType === 'advisory'
  );
  const { data: filterOptions } = useReportsFilterOptions();
  const exportMutation = useIssuedPremiumsExport();

  const rows = results?.data ?? [];
  const pagination = results?.meta;

  const showAdvisoryColumn = userType === 'insurer';
  const showBrokerColumn = userType === 'insurer' || userType === 'advisory';

  const columns = useMemo<DataTableColumn<IssuedPremium>[]>(() => {
    const cols: DataTableColumn<IssuedPremium>[] = [];

    // Larguras compactas: a soma cabe na área de conteúdo sem scroll horizontal
    // (~1340px na visão Seguradora com 13 colunas), como no Figma.
    if (showAdvisoryColumn) {
      cols.push({
        id: 'advisory',
        accessorKey: 'advisoryName',
        header: 'Assessoria',
        size: 150,
        cell: ({ row }) => (
          <TwoLine
            top={row.original.advisoryName}
            bottom={row.original.advisoryTaxId}
          />
        ),
      });
    }
    if (showBrokerColumn) {
      cols.push({
        id: 'broker',
        accessorKey: 'brokerName',
        header: 'Corretor',
        size: 150,
        cell: ({ row }) => (
          <TwoLine
            top={row.original.brokerName}
            bottom={row.original.brokerTaxId}
          />
        ),
      });
    }

    cols.push(
      {
        id: 'product',
        accessorKey: 'productName',
        header: 'Produto',
        size: 95,
        cell: ({ row }) => (
          <span className='text-xs text-alba-black-500'>
            {row.original.productName || '-'}
          </span>
        ),
      },
      {
        id: 'client',
        accessorKey: 'clientName',
        header: 'Cliente',
        size: 150,
        cell: ({ row }) => (
          <TwoLine
            top={row.original.clientName}
            bottom={row.original.clientTaxId}
          />
        ),
      },
      {
        id: 'sub',
        accessorKey: 'subNumber',
        header: 'Sub',
        size: 55,
        sortable: false,
        cell: ({ row }) => (
          <span className='text-xs text-alba-black-500'>
            {row.original.subNumber ?? '-'}
          </span>
        ),
      },
      {
        // Apólice + Endosso agrupados (empilhados) para reduzir a largura do grid.
        id: 'policy',
        accessorKey: 'policyCode',
        header: 'Apólice',
        size: 115,
        cell: ({ row }) => (
          <div className='flex flex-col'>
            <span className='text-xs text-alba-black-500'>
              {row.original.policyCode ?? '-'}
            </span>
            <span className='text-[11px] text-alba-gray-600'>
              End. {row.original.endorsementNumber ?? '-'}
            </span>
          </div>
        ),
      },
      {
        id: 'coverage',
        header: 'Vigência',
        size: 110,
        sortable: false,
        cell: ({ row }) => (
          <div className='flex flex-col gap-1'>
            <DateCell value={row.original.coverageStartDate} />
            <DateCell value={row.original.coverageEndDate} />
          </div>
        ),
      },
      {
        id: 'issue',
        accessorKey: 'issueDate',
        header: 'Emissão',
        size: 105,
        cell: ({ row }) => <DateCell value={row.original.issueDate} />,
      },
      {
        // Total / Pago / Pendente agrupados numa coluna "Prêmio" (3 linhas rotuladas).
        id: 'premium',
        header: 'Prêmio',
        size: 150,
        sortable: false,
        cell: ({ row }) => (
          <div className='flex flex-col gap-0.5 text-xs text-alba-black-500'>
            <span>
              <span className='text-alba-gray-600'>Total </span>
              {money(row.original.totalAmount)}
            </span>
            <span>
              <span className='text-alba-gray-600'>Pago </span>
              {money(row.original.paidAmount)}
            </span>
            <span>
              <span className='text-alba-gray-600'>Pend. </span>
              {money(row.original.pendingAmount)}
            </span>
          </div>
        ),
      },
      {
        id: 'status',
        accessorKey: 'statusName',
        header: 'Status',
        size: 100,
        cell: ({ row }) => {
          const name = row.original.statusName ?? '';
          const cancelled = name.toLowerCase().includes('cancel');
          return (
            <Tag.Root
              variant='outline'
              colorScheme={cancelled ? 'error' : 'success'}
              size='sm'
            >
              <Tag.Label>{name || '-'}</Tag.Label>
            </Tag.Root>
          );
        },
      }
    );

    return cols;
  }, [showAdvisoryColumn, showBrokerColumn]);

  const sorting = useMemo(() => {
    const columnId = COLUMN_BY_SORT_FIELD[filters.sortField];
    if (!columnId) return [];
    return [{ id: columnId, desc: filters.sortType === 'desc' }];
  }, [filters.sortField, filters.sortType]);

  const handleExport = () => {
    if (!periodReady) return;
    exportMutation.mutate({
      ...activeFilters,
      sortField: queryParams.sortField,
      sortType: queryParams.sortType,
    });
  };

  return (
    <div className='flex flex-col gap-6'>
      <SummaryCards summary={summary} loading={summaryLoading && periodReady} />

      <div className='flex flex-col gap-4'>
        <div className='flex flex-col'>
          {/* Barra: busca + botão Filtros (esquerda) | Exportar (direita) na mesma linha. */}
          <div className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
            <div className='flex flex-1 items-center gap-3'>
              <div className='max-w-xl flex-1'>
                <SearchFilterField
                  {...registerFilter('search')}
                  placeholder='Buscar por apólice, CNPJ, nome...'
                  className='w-full'
                />
              </div>
              <button
                type='button'
                onClick={() => setShowFilters(value => !value)}
                aria-expanded={showFilters}
                className='flex h-10 shrink-0 items-center gap-2 rounded-lg border border-alba-gray-400 px-4 text-sm text-alba-gray-600 transition-colors hover:bg-alba-gray-100'
              >
                {showFilters ? (
                  <FunnelX className='h-4 w-4' strokeWidth={1.8} />
                ) : (
                  <Funnel className='h-4 w-4' strokeWidth={1.8} />
                )}
                Filtros
              </button>
            </div>
            <Button
              variant='primary'
              size='md'
              onClick={handleExport}
              disabled={
                !periodReady || !rows.length || exportMutation.isPending
              }
            >
              <Download className='mr-2 h-4 w-4' />
              {exportMutation.isPending ? 'Exportando...' : 'Exportar'}
            </Button>
          </div>

          <PremiosEmitidosFilters
            open={showFilters}
            hasActiveFilter={hasActiveFilter}
            userType={userType}
            values={{
              issueDateFrom: filters.issueDateFrom,
              issueDateTo: filters.issueDateTo,
              advisoryPartyCode: filters.advisoryPartyCode,
              brokerPartyCode: filters.brokerPartyCode,
              productCode: filters.productCode,
              branchCode: filters.branchCode,
              statusCode: filters.statusCode,
            }}
            advisoryOptions={advisoryOptions}
            brokerOptions={brokerOptions}
            productOptions={filterOptions?.products ?? []}
            branchOptions={filterOptions?.branches ?? []}
            statusOptions={filterOptions?.policyStatuses ?? []}
            periodError={periodError}
            onChange={patch => setFilters({ ...filters, ...patch, page: 1 })}
            onClear={() =>
              setFilters({
                ...filters,
                page: 1,
                advisoryPartyCode: '',
                brokerPartyCode: '',
                productCode: '',
                branchCode: '',
                statusCode: '',
                issueDateFrom: defaultPeriod.from,
                issueDateTo: defaultPeriod.to,
              })
            }
          />
        </div>

        {exportMutation.isError && (
          <p className='text-sm text-alba-error-red-500'>
            {(exportMutation.error as Error).message}
          </p>
        )}

        {!periodReady ? (
          <div className='rounded-3xl bg-white p-8 text-center text-alba-gray-600 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'>
            {periodError ??
              'Selecione um período de emissão (até 6 meses) para visualizar os prêmios emitidos.'}
          </div>
        ) : error ? (
          <div className='rounded-3xl bg-white p-8 text-center text-alba-error-red-500 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'>
            Não foi possível carregar os prêmios emitidos. Tente novamente mais
            tarde.
          </div>
        ) : !isLoading && !rows.length ? (
          <div className='rounded-3xl bg-white p-8 text-center text-alba-gray-600 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'>
            Nenhum prêmio emitido encontrado para os critérios informados.
          </div>
        ) : (
          <>
            {/* Mobile: cards no lugar da tabela. */}
            <div className='space-y-4 md:hidden'>
              {isLoading
                ? Array.from({ length: Math.min(filters.pageSize, 3) }).map(
                    (_, index) => <ReportCardSkeleton key={index} />
                  )
                : rows.map((row, index) => (
                    <PremioCard
                      key={`${row.policyCode}-${row.endorsementNumber}-${row.subNumber}-${index}`}
                      row={row}
                      showAdvisory={showAdvisoryColumn}
                      showBroker={showBrokerColumn}
                    />
                  ))}
              {pagination && pagination.totalPages > 0 && (
                <div className='flex justify-center'>
                  <Pagination
                    count={pagination.totalPages * filters.pageSize}
                    page={filters.page}
                    pageSize={filters.pageSize}
                    totalItems={pagination.total}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    onPageChange={page =>
                      setFilters({
                        ...filters,
                        page: page.page,
                        pageSize: page.pageSize,
                      })
                    }
                    onPageSizeSelect={pageSize =>
                      setFilters({ ...filters, page: 1, pageSize })
                    }
                  />
                </div>
              )}
            </div>

            {/* Desktop: tabela. */}
            <div className='hidden min-w-0 overflow-hidden rounded-3xl md:block'>
              <DataTable
                ref={dataTableRef}
                className='w-full'
                loading={isLoading}
                columns={columns}
                data={rows}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                totalItems={pagination?.total ?? 0}
                totalPages={pagination?.totalPages ?? 0}
                pageSize={filters.pageSize}
                manualSorting
                sorting={sorting}
                onSortChange={nextSorting => {
                  const nextSort = nextSorting[0];
                  const sortField = nextSort
                    ? SORT_FIELD_BY_COLUMN[nextSort.id]
                    : '';
                  setFilters({
                    ...filters,
                    page: 1,
                    sortField: sortField ?? '',
                    sortType: nextSort?.desc ? 'desc' : 'asc',
                  });
                }}
                onPageChange={(page, pageSize) =>
                  setFilters({ ...filters, page, pageSize })
                }
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DateCell({ value }: { value: string | null }) {
  return (
    <span className='flex items-center gap-1 text-xs text-alba-black-500'>
      <Calendar className='h-3 w-3 shrink-0 text-alba-gray-500' />
      {formatDateToBrazil(value) || '-'}
    </span>
  );
}

function TwoLine({
  top,
  bottom,
}: {
  top?: string | null;
  bottom?: string | null;
}) {
  return (
    <div className='flex flex-col'>
      <span className='text-xs font-semibold text-alba-black-500'>
        {top || '-'}
      </span>
      {bottom ? (
        <span className='text-[11px] text-alba-gray-600'>{bottom}</span>
      ) : null}
    </div>
  );
}

/** Card mobile de um prêmio emitido (equivalente a uma linha da tabela). */
function PremioCard({
  row,
  showAdvisory,
  showBroker,
}: {
  row: IssuedPremium;
  showAdvisory: boolean;
  showBroker: boolean;
}) {
  const cancelled = (row.statusName ?? '').toLowerCase().includes('cancel');
  return (
    <ReportCard>
      <ReportCardHeader
        title={row.clientName}
        subtitle={row.clientTaxId}
        right={
          <Tag.Root
            variant='outline'
            colorScheme={cancelled ? 'error' : 'success'}
            size='sm'
          >
            <Tag.Label>{row.statusName || '-'}</Tag.Label>
          </Tag.Root>
        }
      />
      <ReportCardBody>
        {showAdvisory && (
          <CardRow label='Assessoria'>
            <span className='block truncate'>{row.advisoryName || '-'}</span>
          </CardRow>
        )}
        {showBroker && (
          <CardRow label='Corretor'>
            <span className='block truncate'>{row.brokerName || '-'}</span>
          </CardRow>
        )}
        <CardRow label='Produto'>
          <span className='block truncate'>{row.productName || '-'}</span>
        </CardRow>
        <CardRow label='Apólice'>{row.policyCode ?? '-'}</CardRow>
        <CardRow label='Endosso'>{row.endorsementNumber ?? '-'}</CardRow>
        <CardRow label='Sub'>{row.subNumber ?? '-'}</CardRow>
        <CardRow label='Vigência'>
          <div className='flex flex-col items-end'>
            <span>{formatDateToBrazil(row.coverageStartDate) || '-'}</span>
            <span>{formatDateToBrazil(row.coverageEndDate) || '-'}</span>
          </div>
        </CardRow>
        <CardRow label='Emissão'>
          {formatDateToBrazil(row.issueDate) || '-'}
        </CardRow>
        <CardRow label='Prêmio Total'>{money(row.totalAmount)}</CardRow>
        <CardRow label='Prêmio Pago'>{money(row.paidAmount)}</CardRow>
        <CardRow label='Prêmio Pendente'>{money(row.pendingAmount)}</CardRow>
      </ReportCardBody>
    </ReportCard>
  );
}
